import type { SchemaDraft, SchemaSettings } from '../../services/inboundSchemasApi';

export type IntegrationTypeFilter = 'all' | 'alert' | 'notification';
export type IntegrationStatusFilter = 'all' | 'enabled' | 'disabled';

export interface IntegrationFilterRow {
  name: string;
  provider: string;
  isEnabled: boolean;
  nonAlert?: boolean;
  transformationType?: string | null;
  baseUrl?: string | null;
  authType?: string | null;
}

export interface IntegrationFilters {
  search: string;
  type: IntegrationTypeFilter;
  status: IntegrationStatusFilter;
}

export type InboundSchemaCoverage =
  | { status: 'ready'; payloadMode: 'Direct'; strictEnabled: boolean; directInputName: string | null; directInputEnabled: boolean }
  | { status: 'ready'; payloadMode: 'Normalise'; strictEnabled: boolean; mappedInputShapes: number; enabledInputShapes: number; outputShapeConfigured: boolean }
  | { status: 'unavailable' };

export function summarizeInboundSchemaCoverage(
  definitions: readonly (Pick<SchemaDraft, 'inputSchemaJson' | 'mappingJson'> & Partial<Pick<SchemaDraft, 'schemaId' | 'name' | 'activeVersion'>>)[],
  settings: Pick<SchemaSettings, 'draftOutputSchemaJson' | 'draftOutputVersion'> & Partial<Pick<SchemaSettings, 'payloadMode' | 'strictEnabled' | 'directSchemaId' | 'directVersion'>>,
): InboundSchemaCoverage {
  const strictEnabled = settings.strictEnabled === true;
  if (settings.payloadMode === 'Direct') {
    const selected = definitions.find((definition) => definition.schemaId?.toLowerCase() === settings.directSchemaId?.toLowerCase()
      && !!settings.directSchemaId);
    let inputConfigured = false;
    try {
      const input = selected && JSON.parse(selected.inputSchemaJson) as { type?: unknown };
      inputConfigured = !!input && typeof input === 'object' && !Array.isArray(input) && input.type === 'object';
    } catch {
      inputConfigured = false;
    }
    return {
      status: 'ready', payloadMode: 'Direct', strictEnabled,
      directInputName: inputConfigured ? selected?.name || 'Selected schema' : null,
      directInputEnabled: inputConfigured && (settings.directVersion ?? 0) > 0,
    };
  }

  const mappedDefinitions = definitions.filter((definition) => {
    try {
      const input = JSON.parse(definition.inputSchemaJson) as { type?: unknown };
      const mapping = JSON.parse(definition.mappingJson) as Record<string, unknown>;
      return !!input && typeof input === 'object' && !Array.isArray(input)
        && input.type === 'object' && !!mapping && typeof mapping === 'object' && !Array.isArray(mapping)
        && Object.keys(mapping).length > 0;
    } catch {
      return false;
    }
  });

  let outputShapeConfigured = false;
  try {
    const output = JSON.parse(settings.draftOutputSchemaJson) as { properties?: unknown };
    const properties = output?.properties;
    outputShapeConfigured = !!properties && typeof properties === 'object' && !Array.isArray(properties)
      && Object.keys(properties).length > 0;
  } catch {
    outputShapeConfigured = false;
  }

  return {
    status: 'ready',
    payloadMode: 'Normalise',
    strictEnabled,
    mappedInputShapes: mappedDefinitions.length,
    enabledInputShapes: mappedDefinitions.filter((definition) => (definition.activeVersion ?? 0) > 0).length,
    outputShapeConfigured,
  };
}

export const createIntegrationFilters = (): IntegrationFilters => ({ search: '', type: 'all', status: 'all' });

export function getTransformationDisplayName(transformationType?: string | null): string {
  switch (transformationType?.toLowerCase()) {
    case 'default': return 'Legacy default (Auvik-compatible fallback)';
    case 'jsonata': return 'Legacy JSONata (unsupported by runtime)';
    case 'mappings': return 'Legacy Field Mappings';
    default: return transformationType || 'Unknown';
  }
}

export function getTransformationSummary(transformationType?: string | null): string {
  switch (transformationType?.toLowerCase()) {
    case 'default': return 'Default fallback';
    case 'jsonata': return 'JSONata (unsupported)';
    case 'mappings': return 'Field mappings';
    default: return transformationType || 'Unknown';
  }
}

export function filterIntegrations<Row extends IntegrationFilterRow>(
  rows: readonly Row[],
  direction: 'inbound' | 'outbound',
  filters: IntegrationFilters = createIntegrationFilters(),
): Row[] {
  const query = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.status !== 'all' && row.isEnabled !== (filters.status === 'enabled')) return false;
    if (direction === 'inbound' && filters.type !== 'all' && (row.nonAlert ?? false) !== (filters.type === 'notification')) return false;
    const transformation = getTransformationDisplayName(row.transformationType);
    const fields = direction === 'inbound'
      ? [row.name, row.provider, row.transformationType, transformation]
      : [row.name, row.provider, row.baseUrl, row.authType];
    return !query || fields.some((field) => field?.toLowerCase().includes(query));
  });
}